import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import { Card, CardContent, Badge, Button, TableSkeleton } from '@/components/ui';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiErrorDisplay } from '@/components/features/error';
import { WebhookFilters } from '@/components/features/webhooks';
import { useWebhooks } from '@/hooks/use-webhooks';
import { formatDate } from '@/lib/utils';
import type { ListWebhooksParams, WebhookStatus } from '@/types/webhook';

const DEFAULT_LIMIT = 10;

/**
 * Get badge variant for webhook status.
 */
function getStatusBadgeVariant(status: WebhookStatus) {
  switch (status) {
    case 'received':
      return 'info';
    case 'processed':
      return 'success';
    case 'failed':
      return 'destructive';
    default:
      return 'secondary';
  }
}

/**
 * Webhook list page.
 * Route: /webhooks
 */
export function WebhooksPage() {
  const { t } = useTranslation('webhooks');
  const [filters, setFilters] = React.useState<ListWebhooksParams>({
    page: 1,
    limit: DEFAULT_LIMIT,
  });

  const { data, isLoading, error, refetch } = useWebhooks(filters);

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  return (
    <div>
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
      />

      <Card>
        <CardContent className="space-y-4 pt-6">
          {/* Filters */}
          <WebhookFilters filters={filters} onFiltersChange={setFilters} />

          {/* Loading State */}
          {isLoading && <TableSkeleton rows={5} cols={5} />}

          {/* Error State */}
          {error && <ApiErrorDisplay error={error} onRetry={() => refetch()} />}

          {/* Data Table */}
          {data && !isLoading && !error && (
            <>
              {data.data.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  {t('list.empty')}
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('table.eventId')}</TableHead>
                        <TableHead>{t('table.eventType')}</TableHead>
                        <TableHead>{t('table.transactionId')}</TableHead>
                        <TableHead>{t('table.status')}</TableHead>
                        <TableHead>{t('table.received')}</TableHead>
                        <TableHead className="w-[80px]">{t('table.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.data.map((webhook) => (
                        <TableRow key={webhook.eventId}>
                          <TableCell className="font-mono text-sm">
                            {webhook.eventId}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {t(`eventTypes.${webhook.eventType}`, { defaultValue: webhook.eventType })}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {webhook.transactionId}
                          </TableCell>
                          <TableCell>
                            <Badge variant={getStatusBadgeVariant(webhook.status)}>
                              {t(`common:status.${webhook.status}`)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(webhook.receivedAt)}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" asChild>
                              <Link
                                to={`/webhooks/${webhook.eventId}`}
                                aria-label={t('list.viewDetails', { eventId: webhook.eventId })}
                              >
                                <Eye className="h-4 w-4" />
                              </Link>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {t('list.showing', {
                        from: (data.pagination.page - 1) * data.pagination.limit + 1,
                        to: Math.min(
                          data.pagination.page * data.pagination.limit,
                          data.pagination.total
                        ),
                        total: data.pagination.total,
                      })}
                    </p>
                    <Pagination
                      page={data.pagination.page}
                      totalPages={data.pagination.totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
